// Flusher do outbox — envia os itens pendentes pro servidor com DEBOUNCE +
// flush em eventos. Plan: docs/plan/sync-conta-cross-device.md (S3.2).
//
// PREMISSA DE INFRA (inegociável — memória feedback_cuidado_cache_custo):
//   - Posições mudam durante a reprodução → NÃO 1 request por segundo. O
//     enqueue debouncia o flush (30s); o outbox deduplica (mantém só o estado
//     mais recente por item). 1 flush envia o lote inteiro em poucos requests.
//   - Flush também em eventos relevantes: aba escondida (visibilitychange) e
//     antes de fechar (pagehide) — pra não perder progresso ao sair.
//
// O flusher só roda se há sessão Swipe (getValidSwipeAccessToken). Sem conta
// logada, o outbox simplesmente acumula (ou nem enfileira — ver enqueueWrite).
//
// SÓ O QUE É DA CONTA DA SESSÃO (AP-116, ver "O DONO" em outbox.ts): o flush manda e
// PROVISIONA o perfil (`ensureRemoteProfile`) só pras entradas com `owner === conta
// atual`. Entrada de outra conta fica (é de quem a enfileirou; sai quando ela voltar,
// inclusive depois de uma troca direta de conta: ver `loginSwipeSession`).
//
// A CONTA NÃO PODE TROCAR NO MEIO DO FLUSH (AP-116, sonda AP3 do cybersec). O flush é
// longo (vários pedidos) e guardava a conta só no início, enquanto o token era lido de
// novo a cada pedido. Com o 1º pedido pendurado, o sign-out zerava o outbox em disco, B
// pareava, e o flush retomava com os grupos de A em memória: PUT do perfil de A com o
// token de B (criando o perfil na conta de B) e o POST do histórico de A. Agora todo
// pedido passa por `request`, que confere a SESSÃO (a geração, swipe-session.ts) ANTES
// e DEPOIS dele e aborta o flush se ela mudou. Sessão e não conta: os grupos foram
// drenados nesta sessão, e um sign-out os apagou do disco; uma sessão nova (mesmo da
// mesma conta) drena os dela. Toda troca de conta passa por um pareamento, que muda a
// geração. E o pedido em si só sai com o token da conta dona das entradas (`asUser` no
// sync-api): ali a pergunta é de CONTA (o dono do dado contra o dono do token). Todo
// pedido tem prazo por corrida (sync/net.ts).
//
// A ENTRADA SEM DONO (de antes desta versão; nenhuma nasce mais assim) nunca provisiona
// o perfil. O veredito é do `GET /profiles` da conta da sessão, lido uma vez por sessão:
//   - o perfil é da conta → sai (sem PUT);
//   - não é → é DESCARTADA na hora. Não fica em quarentena esperando: ela só sairia
//     pra conta dona dela, e só se essa conta logasse depois da sessão atual morrer sem
//     sign-out. Esperando, custava um `GET /profiles` e um evento por flush pra sempre
//     (sonda AP4 do cybersec e Q-A do reviewer). O dado segue no aparelho; a próxima
//     escrita do mesmo item já nasce com dono.
//   - sem resposta (rede) → o grupo inteiro espera o próximo flush, SEM provisionar o
//     perfil: o veredito vem antes de qualquer PUT do perfil. Senão o PUT das entradas
//     da sessão num perfil local de outra conta (B escolhendo o perfil de A na tela de
//     perfis, AP-148) criava o perfil na conta de B, e o `GET /profiles` seguinte o
//     listaria como de B, liberando o legado de A.
//   - e nunca sai pra um perfil que ESTE aparelho criou pelo lazy-provision (o
//     endurecimento pedido pelo cybersec), por construção: a lista do veredito é lida
//     UMA vez por sessão, ANTES de qualquer PUT (o `ensureRemoteProfile` a lê primeiro, e
//     sem ela não provisiona), e não é relida na sessão. Um perfil criado por um PUT desta
//     TV nunca está nela. E o perfil que já estava nela não leva PUT (sonda R3: antes, com
//     a conta em dois provedores, o PUT de um grupo marcava o perfil como "criado por
//     esta TV" e o legado dela no outro grupo era descartado).
//
// O ITEM ENVENENADO (10/10/2026). Um item que o servidor recusa pelo CONTEÚDO (400 do
// zod: payload acima de 16 KB, itemKey fora do tamanho) virava `null`, o flush abortava
// inteiro e repetia o MESMO lote pra sempre: um item travava o push do aparelho todo.
// Agora, por grupo:
//   1. o que a validação local (push-limits.ts, espelho do servidor) reprova nem sai;
//   2. a recusa TERMINAL de um lote (400/413/422, ver `PUSH_TERMINAL_STATUSES`) o parte
//      ao meio até isolar o culpado, com teto de partições por flush;
//   3. o culpado vai pra QUARENTENA (outbox.ts): sai da fila de envio e fica guardado
//      pra diagnóstico, com telemetria `sync_item_quarantined` (contagem). O isolado por
//      resposta do SERVIDOR só vira quarentena no fim do flush e com prova de que o
//      servidor aceita item bom; recusa em vários grupos sem nenhum sucesso para o flush
//      (ver SYSTEMIC_TERMINAL_GROUPS: um 400 sistêmico não esvazia o outbox);
//   4. o flush SEGUE com os outros grupos. 5xx, rede, prazo e 401 continuam parando o
//      flush pra tentar de novo depois (não dizem nada sobre o item).

import type { Profile } from '../local-db'
import { track } from '../qoe'

import { currentSyncBoot } from './boot-count'
import { SYNC_CLOCK_MIN_MS } from './clock'
import { ackSent, discardIfUnchanged, drainGroups, type OutboxEntry, quarantineIfUnchanged } from './outbox'
import { type LocalRejection, localRejection } from './push-limits'
import { getSessionGeneration, getSwipeUserId, getValidSwipeAccessToken } from './swipe-session'
import { listRemoteProfiles, pushSync, type PushItem, upsertRemoteProfile } from './sync-api'

const DEBOUNCE_MS = 30_000
const BATCH_SIZE = 200 // = MAX_SYNC_BATCH do backend

/**
 * Teto de partições de lote recusado (bisseção) por flush. Isolar UM item envenenado
 * num lote de 200 custa ~log2(200) = 8 partições (cada uma, 2 pedidos); 32 isolam uns 4
 * por flush sem transformar um defeito do servidor (tudo recusado) numa rajada de
 * centenas de pedidos contra o rate-limit do sync. O que sobra espera o próximo flush.
 */
export const MAX_SPLITS_PER_FLUSH = 32

/**
 * Circuito do 400 SISTÊMICO (observação do cybersec, 10/10/2026). Se o servidor passar a
 * recusar TODO pedido com 400 (deploy com schema mais estrito), a bisseção isolaria cada
 * item como "envenenado" e o outbox inteiro iria pra quarentena aos poucos, com o teto da
 * quarentena apagando o excedente: alterações perdidas por um defeito do SERVIDOR.
 * Duas travas:
 *  - a quarentena por resposta do servidor só é gravada no FIM do flush, e só se algum
 *    push deste flush deu certo (prova de que o servidor aceita item bom e o culpado é
 *    mesmo o item). Sem essa prova os suspeitos ficam pendentes pro próximo flush;
 *  - com recusa terminal em `SYSTEMIC_TERMINAL_GROUPS` grupos e NENHUM sucesso no flush,
 *    o flush para (como um 404/405/410: retry depois), sem gastar mais pedidos.
 * A validação local (push-limits.ts) não passa por aqui: ela não depende do servidor.
 */
export const SYSTEMIC_TERMINAL_GROUPS = 3

/**
 * Teto de recusas terminais SEM nenhum sucesso no flush (cybersec P2, rodada 3). Só o
 * teto por grupos não continha a amplificação: com o servidor recusando tudo e UM grupo
 * de 200 no outbox, cada flush gastava as 32 partições (~60 pedidos) antes de o circuito
 * abrir — antes do conserto era 1. Dez é log2(200) + 1: o pior caminho até isolar UM item
 * envenenado no começo de um lote cheio são 9 recusas seguidas (200, 100, 50, 25, 13, 7,
 * 4, 2, 1) antes do primeiro sucesso, então o envenenado sozinho continua sendo isolado.
 */
export const MAX_TERMINAL_WITHOUT_SUCCESS = 10

// ── A posição da sonda (rodada 5) ──
// Um contador que avança a cada sonda e sobrevive entre flushes (e, com localStorage,
// entre aberturas do app). A posição é a parte fracionária de `k·φ` (razão áurea) vezes o
// tamanho do grupo: a sequência de Weyl mais uniforme que existe, então N sondas caem
// espalhadas pelo grupo inteiro em vez de repetirem a mesma ponta.
const PROBE_SEQ_KEY = 'iptv:sync-probe-seq'
const GOLDEN = 0.6180339887498949
let probeSeqMemory = 0

/** O índice da próxima sonda num grupo de `n` itens (avança o contador). */
export function nextProbeIndex(n: number): number {
  let k = probeSeqMemory
  try {
    const saved = Number(localStorage.getItem(PROBE_SEQ_KEY))
    if (Number.isFinite(saved) && saved > k) k = saved
  } catch {
    // sem localStorage (teste, privado): só a memória
  }
  k += 1
  probeSeqMemory = k
  try {
    localStorage.setItem(PROBE_SEQ_KEY, String(k))
  } catch {
    // idem
  }
  return Math.min(n - 1, Math.floor(((k * GOLDEN) % 1) * n))
}

// ── O prazo da entrada de OUTRA conta (TV compartilhada; review de 29/09/2026) ──
// A entrada de outra conta fica no outbox e sai quando aquela conta voltar. Uma conta que
// saiu sem sign-out e nunca volta deixava a sobra pra sempre. Vence quando passou TEMPO
// E BOOTS desde a escrita, ou só um teto de boots. O que se perde é só a SUBIDA daquelas
// escritas pro servidor: o dado local fica na TV, e a próxima escrita do mesmo item, se
// a conta voltar, sobe normalmente.
// Robusto ao relógio da TV (sync/boot-count.ts): um relógio adiantado sozinho não vence
// nada (faltam os boots); um relógio atrasado ou parado não impede o fim (o teto de boots).
// E a escrita feita com o relógio antes de SYNC_CLOCK_MIN_MS (TV sem RTC em 1970, antes do
// NTP) não tem idade confiável: pra ela só vale o teto de boots (senão o NTP, ao acertar,
// dava ~56 anos de "idade" a uma sobra de horas).
// Limites aceitos (review de 29/09/2026): um relógio ADIANTADO, junto com 20 boots, vence
// sobra de poucos dias (o cliente não tem como saber que o relógio está à frente); e o
// teto de 200 boots vence sem olhar o tempo (um loop de crash que reinicia o app chega
// lá). Os dois só custam a subida daquelas escritas; o dado local fica.
/** Tempo mínimo desde a escrita: um mês. Uma conta da família que usa a TV de vez em
 *  quando (fim de semana, férias) volta antes disso. */
export const OTHER_OWNER_TTL_MIN_MS = 30 * 24 * 60 * 60 * 1000
/** Boots mínimos desde a escrita: 20 execuções do app. Exige uso de verdade: a TV que
 *  ficou um mês desligada não apaga nada no 1º boot de volta. */
export const OTHER_OWNER_TTL_MIN_BOOTS = 20
/** Teto só de boots, independente do relógio: numa TV ligada 5× por dia, ~40 dias. */
export const OTHER_OWNER_TTL_HARD_BOOTS = 200

/** A entrada de outra conta venceu o prazo? Sem a contagem de boots, nunca vence. */
function otherOwnerExpired(e: OutboxEntry, nowMs: number, boot: number | null): boolean {
  if (boot === null || e.enqueuedBoot === undefined) return false
  const boots = boot - e.enqueuedBoot
  if (boots >= OTHER_OWNER_TTL_HARD_BOOTS) return true
  // Escrita com o relógio de antes do piso: a idade não é confiável (só o teto acima).
  if (e.enqueuedAt < SYNC_CLOCK_MIN_MS) return false
  return boots >= OTHER_OWNER_TTL_MIN_BOOTS && nowMs - e.enqueuedAt >= OTHER_OWNER_TTL_MIN_MS
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null
let flushInflight: Promise<void> | null = null
let listenersBound = false

// Perfis já provisionados no servidor NESTA execução do app (evita 1 upsert por
// flush). Resetado no clearOutbox/troca de conta (via reset explícito abaixo).
const provisionedProfiles = new Set<string>()

/**
 * Perfis que o servidor disse NÃO serem da conta da sessão (403
 * `profile_not_owned`). O flush PULA esses grupos — sem abortar os outros e sem
 * apagar as entradas (elas são de outra conta que usou esta TV e podem sair quando
 * ela voltar). Vale até a troca de conta (`resetProvisionedCache`).
 */
const notOwnedProfiles = new Set<string>()

/** Os perfis da conta da sessão no servidor, lidos UMA vez por sessão, ANTES de qualquer
 *  PUT do lazy-provision, e nunca relidos na sessão (o veredito da entrada sem dono; ver
 *  o cabeçalho). `null` = ainda não vieram. */
let accountProfiles: Set<string> | null = null

/** A geração da sessão (swipe-session.ts) dos caches acima. Defesa em profundidade: hoje
 *  todo caminho que troca a sessão passa pelo `loginSwipeSession`, que já zera os caches;
 *  a geração garante que uma sessão nova começa com eles vazios mesmo por um caminho que
 *  não passe por lá. Sem isso, a lista de perfis e os recusados da conta de antes
 *  seguiriam valendo pra conta nova. */
let cachesGeneration: number | null = null

/** Limpa os caches da sessão (troca de conta/logout): provisionados, recusados e a
 *  lista de perfis da conta. */
export function resetProvisionedCache(): void {
  provisionedProfiles.clear()
  notOwnedProfiles.clear()
  accountProfiles = null
}

/** A conta trocou no meio do flush (ver o cabeçalho). */
const ABORTED = Symbol('sync-flush-aborted')

/** O flush em curso: a conta dona das entradas que ele manda e a SESSÃO em que começou. */
interface FlushCtx {
  me: string
  generation: number
}

/**
 * Todo pedido do flush passa por aqui: só sai se a sessão ainda é a do flush, e o
 * resultado só é usado se ela continua sendo depois. Senão o flush inteiro para
 * (`ABORTED`). O `asUser` do sync-api confere a conta sobre o token.
 */
async function request<T>(ctx: FlushCtx, send: () => Promise<T>): Promise<T> {
  if (getSessionGeneration() !== ctx.generation) throw ABORTED
  const out = await send()
  if (getSessionGeneration() !== ctx.generation) throw ABORTED
  return out
}

/**
 * Garante que o perfil (id LOCAL) existe no servidor antes do push. Import
 * DINÂMICO do local-db pra evitar ciclo (local-db → sync → flusher → local-db).
 * Retorna true se o perfil está provisionado (ou já estava). Best-effort.
 */
async function ensureRemoteProfile(ctx: FlushCtx, providerCode: string, profileId: string): Promise<boolean> {
  const me = ctx.me
  if (provisionedProfiles.has(profileId)) return true
  // A lista da conta ANTES do primeiro PUT (sonda R3 do reviewer, 29/09/2026): sem ela,
  // o PUT de um perfil que a conta JÁ tinha contava como "criado por esta TV" e, noutro
  // grupo do mesmo perfil (outro provedor da conta), o legado sem dono dela era
  // descartado. Com ela: o perfil que já é da conta não precisa de PUT, e o PUT só
  // acontece pro perfil que não estava na lista, depois que a lista do veredito foi
  // lida (ver o cabeçalho). Sem a lista (rede), não provisiona neste flush.
  const known = await profilesOfAccount(ctx)
  if (known === null) return false
  if (known.has(profileId)) {
    provisionedProfiles.add(profileId)
    return true
  }
  let local: Profile | undefined
  try {
    const { getProfileById } = await import('../local-db')
    local = await getProfileById(profileId)
  } catch {
    return false
  }
  // Sem perfil local (excluído?) → manda um upsert mínimo só pra existir a FK.
  const res = await request(ctx, () =>
    upsertRemoteProfile(
      profileId,
      {
        name: local?.name ?? 'Perfil',
        avatar: local?.avatar ?? null,
        kind: local?.kind ?? 'adult',
        providerCode,
        ...(local ? { updatedAt: new Date(local.createdAt * 1000).toISOString() } : {}),
      },
      { asUser: me },
    ),
  )
  if (!res) return false
  provisionedProfiles.add(profileId)
  return true
}

/** Os perfis da conta (uma vez por sessão). `null` = a lista não veio (rede). */
async function profilesOfAccount(ctx: FlushCtx): Promise<Set<string> | null> {
  if (accountProfiles === null) {
    const list = await request(ctx, () => listRemoteProfiles(undefined, { asUser: ctx.me }))
    if (list === null) return null
    accountProfiles = new Set(list.filter((p) => !p.deletedAt).map((p) => p.id))
  }
  return accountProfiles
}

/** Agenda um flush daqui a DEBOUNCE_MS (reinicia o timer se já agendado). */
export function scheduleFlush(): void {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    debounceTimer = null
    void flushNow()
  }, DEBOUNCE_MS)
}

/**
 * Flush imediato (idempotente: chamadas concorrentes compartilham 1 inflight).
 * Drena o outbox por grupo (pc,pid), envia em lotes de 200 e dá ack dos enviados.
 * Best-effort: erro de rede mantém os itens no outbox pra a próxima tentativa.
 */
export async function flushNow(): Promise<void> {
  if (flushInflight) return flushInflight
  flushInflight = (async () => {
    let dropped = 0
    let expiredOthers = 0
    // Partições de lote recusado ainda permitidas neste flush (ver MAX_SPLITS_PER_FLUSH).
    let splitsLeft = MAX_SPLITS_PER_FLUSH
    // Pushes aceitos neste flush, grupos com recusa terminal e os itens isolados como
    // suspeitos (ver SYSTEMIC_TERMINAL_GROUPS).
    let okPushes = 0
    let terminalResponses = 0
    // Ligado quando o teto de recusas sem sucesso estoura (ver MAX_TERMINAL_WITHOUT_SUCCESS).
    let probing = false
    const terminalGroups = new Set<string>()
    const suspects: Array<{ entry: OutboxEntry; status: number; reason: string }> = []
    // Quarentena deste flush, agregada por (motivo, status) pra telemetria (contagem).
    const quarantined = new Map<string, { reason: string; status: number | null; entries: number }>()
    const quarantine = async (list: OutboxEntry[], status: number | null, reason: string): Promise<void> => {
      const n = await quarantineIfUnchanged(
        list.map((e) => ({ id: e.id, updatedAt: e.updatedAt })),
        { at: Date.now(), status, reason },
      )
      if (n === 0) return
      const key = `${reason}|${status ?? 'local'}`
      const cur = quarantined.get(key) ?? { reason, status, entries: 0 }
      cur.entries += n
      quarantined.set(key, cur)
      console.warn(`[sync] ${n} item(ns) em quarentena (${reason}, ${status ?? 'local'})`)
    }
    try {
      // Sem sessão Swipe → não há pra onde enviar; deixa o outbox quieto.
      const token = await getValidSwipeAccessToken()
      if (!token) return

      // A conta da sessão: o DONO que este flush pode mandar (ver o cabeçalho). E a
      // sessão em que ele começou.
      const me = getSwipeUserId()
      if (me === null) return
      const ctx: FlushCtx = { me, generation: getSessionGeneration() }
      if (cachesGeneration !== ctx.generation) {
        resetProvisionedCache()
        cachesGeneration = ctx.generation
      }

      const groups = await drainGroups()
      const boot = currentSyncBoot()
      const nowMs = Date.now()
      groupLoop: for (const group of groups) {
        // A entrada de OUTRA conta que venceu o prazo sai (ver o cabeçalho). Só local:
        // nenhum pedido. Conta só o que o IDB aceitou.
        // A quarentena também vence o prazo de outra conta (cybersec P2): o `drainGroups`
        // entrega as entradas marcadas justamente pra elas não ficarem fora desta conta.
        const expired = group.entries.filter(
          (e) => e.owner !== undefined && e.owner !== me && otherOwnerExpired(e, nowMs, boot),
        )
        if (expired.length > 0 && (await discardIfUnchanged(expired.map((e) => ({ id: e.id, updatedAt: e.updatedAt }))))) {
          expiredOthers += expired.length
        }
        // Perfil de OUTRA conta (já recusado nesta sessão): não tenta de novo.
        if (notOwnedProfiles.has(group.profileId)) continue
        // Em quarentena não sai mais (ver "QUARENTENA" em outbox.ts).
        const live = group.entries.filter((e) => e.quarantined === undefined)
        const mine = live.filter((e) => e.owner === me)
        const ownerless = live.filter((e) => e.owner === undefined)
        if (mine.length === 0 && ownerless.length === 0) continue

        // 1. O veredito da entrada sem dono vem ANTES de qualquer PUT deste perfil.
        let sendOwnerless = false
        if (ownerless.length > 0) {
          const owned = await profilesOfAccount(ctx)
          // Sem veredito: nem provisiona nem manda nada deste grupo (ver o cabeçalho).
          if (owned === null) continue
          if (owned.has(group.profileId)) {
            sendOwnerless = true
          } else {
            // Conta só o descarte que o IDB aceitou; o que falhou volta no próximo flush
            // (sem novo GET: a lista é da sessão).
            if (await discardIfUnchanged(ownerless.map((e) => ({ id: e.id, updatedAt: e.updatedAt })))) {
              dropped += ownerless.length
            }
          }
        }

        // 2. As entradas da sessão: PROVISIONA o perfil remoto antes do push. O
        // profileId é o uuid LOCAL (gerado no createProfile); o backend exige que
        // exista em swipe_profiles, senão o push toma 403. Normalmente o
        // createProfile já fez o upsert, mas perfis legados (criados antes do sync)
        // ou upsert que falhou offline precisam deste lazy-provision. Idempotente.
        const toSend: OutboxEntry[] = []
        if (mine.length > 0 && (await ensureRemoteProfile(ctx, group.providerCode, group.profileId))) {
          toSend.push(...mine) // não deu pra provisionar → tenta no próximo flush
        }
        if (sendOwnerless) toSend.push(...ownerless)
        if (toSend.length === 0) continue

        // 3. Validação LOCAL (espelho dos limites do servidor, push-limits.ts): o que o
        // servidor recusaria com certeza nem sai. Vai direto pra quarentena.
        const sendable: OutboxEntry[] = []
        const localBad = new Map<LocalRejection, OutboxEntry[]>()
        for (const e of toSend) {
          const why = localRejection(e)
          if (why === null) {
            sendable.push(e)
            continue
          }
          const list = localBad.get(why) ?? []
          list.push(e)
          localBad.set(why, list)
        }
        for (const [why, list] of localBad) await quarantine(list, null, why)

        // O backend agrupa o push por profileId; aqui empurramos por (pc,pid).
        // (O providerCode não vai no corpo — o backend resolve via perfil; o
        // profileId é o que isola. Mantemos pc no outbox só pra agrupar/limpar.)
        //
        // Fila de lotes, e não um `for` fixo: a recusa TERMINAL de um lote (ver
        // `PUSH_TERMINAL_STATUSES` no sync-api) o parte ao meio e as duas metades voltam
        // pra frente da fila, até isolar o item culpado (lote de 1), que vai pra
        // quarentena. As metades boas sobem normalmente.
        // A sonda da ponta oposta deste grupo já foi gasta? (ver o teto de recusas)
        let groupProbed = false
        const batches: OutboxEntry[][] = []
        for (let i = 0; i < sendable.length; i += BATCH_SIZE) batches.push(sendable.slice(i, i + BATCH_SIZE))
        while (batches.length > 0) {
          const slice = batches.shift() as OutboxEntry[]
          const items: PushItem[] = slice.map((e: OutboxEntry) => ({
            kind: e.kind,
            itemKey: e.itemKey,
            payload: e.payload,
            updatedAt: e.updatedAt,
            deleted: e.deleted,
          }))
          const res = await request(ctx, () => pushSync(group.profileId, items, { asUser: me }))
          if (res === null) {
            // Falhou (rede/5xx/sessão/prazo) → para o flush; tenta tudo no próximo. Não é
            // recusa do item: repetir é o certo.
            return
          }
          if ('terminal' in res) {
            terminalGroups.add(group.profileId)
            terminalResponses += 1
            if (okPushes === 0 && probing) {
              // O teto já tinha estourado, a sonda do próprio grupo foi recusada e a sonda
              // de OUTRO grupo também: grupos diferentes recusando tudo é o servidor, não os
              // itens. Para o flush sem quarentena.
              console.warn(`[sync] sonda de outro grupo também recusada sem nenhum sucesso: flush parado, sem quarentena`)
              track({ type: 'sync_group_rejected', ts: Math.floor(Date.now() / 1000), reason: 'systemic_terminal', entries: terminalResponses })
              return
            }
            if (slice.length === 1) {
              // Item isolado: o SUSPEITO. Vira quarentena no fim do flush, se algum push
              // deste flush provar que o servidor aceita item bom (ver o circuito).
              const only = slice[0] as OutboxEntry
              suspects.push({ entry: only, status: res.terminal, reason: res.error ?? `http_${res.terminal}` })
            }
            if (okPushes === 0 && terminalResponses >= MAX_TERMINAL_WITHOUT_SUCCESS) {
              // Teto de recusas sem nenhum sucesso. Antes de desistir do grupo, UMA sonda de
              // UM item, numa posição que MUDA a cada flush (`nextProbeIndex`).
              // Rodadas 4 e 5: itens recusados CONTÍGUOS (o outbox sai na ordem da chave
              // `pc::perfil::kind::itemKey`, então um `kind` novo que o servidor ainda não
              // aceita vem todo junto) faziam a bisseção gastar o teto na descida pela
              // esquerda, pra sempre. A sonda pela ponta oposta (rodada 4) só fechava o caso
              // "recusados no começo e bons no fim"; com recusados nas DUAS pontas ela
              // também batia num ruim, toda vez. A posição andando por uma sequência de baixa
              // discrepância cobre o grupo inteiro em poucos flushes: qualquer faixa de bons
              // acaba sondada. Sonda aceita = prova de saúde: o flush segue (bisseção com o
              // orçamento de sempre) e os suspeitos vão pra quarentena no fim. Recusada, o
              // grupo para e o próximo grupo ganha a sonda dele.
              const pool = groupProbed ? [] : batches.flat()
              if (pool.length > 0) {
                groupProbed = true
                const idx = nextProbeIndex(pool.length)
                const probe = pool[idx] as OutboxEntry
                const rest = pool.filter((_, i) => i !== idx)
                batches.length = 0
                batches.push([probe])
                for (let i = 0; i < rest.length; i += BATCH_SIZE) batches.push(rest.slice(i, i + BATCH_SIZE))
                continue
              }
              probing = true
              continue groupLoop
            }
            if (okPushes === 0 && terminalGroups.size >= SYSTEMIC_TERMINAL_GROUPS) {
              // Circuito aberto: nenhum sucesso e recusa terminal em vários grupos. É o
              // servidor, não os itens. Para o flush SEM quarentena (os suspeitos ficam
              // pendentes) e tenta de novo depois.
              console.warn(`[sync] ${terminalResponses} recusas terminais em ${terminalGroups.size} grupos sem nenhum sucesso: flush parado, sem quarentena`)
              track({ type: 'sync_group_rejected', ts: Math.floor(Date.now() / 1000), reason: 'systemic_terminal', entries: terminalResponses })
              return
            }
            if (slice.length === 1) continue
            if (splitsLeft <= 0) {
              // Orçamento de partições deste flush acabou: o resto deste grupo espera o
              // próximo flush (que retoma a bisseção sem os já isolados). Os OUTROS
              // grupos seguem — é isso que o `return` antigo travava.
              console.warn(`[sync] bisseção do lote recusado adiada pro próximo flush: ${slice.length} itens`)
              continue groupLoop
            }
            splitsLeft -= 1
            const mid = Math.ceil(slice.length / 2)
            batches.unshift(slice.slice(0, mid), slice.slice(mid))
            continue
          }
          // 403 `profile_not_owned`: o grupo é de OUTRA conta. Antes, esse 403 virava
          // `null` e abortava o flush INTEIRO — um grupo alheio no outbox travava calado
          // o sync da conta da sessão (medido pelo cybersec, 27/09/2026). Pula SÓ este
          // grupo e segue. As entradas FICAM: não é desta conta apagar dado da outra.
          if ('rejected' in res) {
            notOwnedProfiles.add(group.profileId)
            // Deixa rastro (review, 28/09/2026): pulado calado, um aparelho com dado de
            // outra conta no outbox é indistinguível de um sync saudável. Contagem, sem
            // id de perfil nem itemKey.
            console.warn(`[sync] grupo de outra conta pulado (403 profile_not_owned): ${toSend.length} itens`)
            track({
              type: 'sync_group_rejected',
              ts: Math.floor(Date.now() / 1000),
              reason: res.rejected,
              entries: toSend.length,
            })
            continue groupLoop
          }
          // Prova em runtime da limpeza dos carimbos: o que o servidor fez com o lote
          // que levava tombstones dela. `ignored > 0` NÃO é falha (tombstone ancorado no
          // carimbo perde pra escrita legítima posterior, como deve).
          const stampTombstones = slice.filter((e) => e.origin === 'stamp-migration').length
          if (stampTombstones > 0) {
            track({
              type: 'stamp_migration_push',
              ts: Math.floor(Date.now() / 1000),
              applied: res.applied,
              ignored: res.ignored,
              tombstones: stampTombstones,
              batch: slice.length,
            })
          }
          // Sucesso → ack (remove do outbox só os que não mudaram durante o envio).
          okPushes += 1
          await ackSent(slice.map((e) => ({ id: e.id, updatedAt: e.updatedAt })))
        }
      }
      // Fim do flush: os suspeitos isolados por resposta do servidor só viram quarentena
      // com a prova de que o servidor aceita item bom (algum push deu certo neste flush).
      // Sem ela, ficam pendentes: um item ruim sozinho no outbox não trava ninguém, e o
      // primeiro flush com um item bom junto o isola de vez.
      if (okPushes > 0) {
        for (const sus of suspects) await quarantine([sus.entry], sus.status, sus.reason)
      }
    } catch (err) {
      // A sessão mudou no meio (ver `request`): o flush para. O resto fica no outbox,
      // com o dono dele. Qualquer outro erro é um defeito e sobe.
      if (err !== ABORTED) throw err
    } finally {
      // Rastro do descarte (contagem, sem id): entrada sem dono de perfil que não é da
      // conta da sessão. Uma vez, na hora do descarte; a falha de rede no veredito não
      // conta (o grupo só espera o próximo flush).
      if (dropped > 0) {
        track({ type: 'sync_group_rejected', ts: Math.floor(Date.now() / 1000), reason: 'ownerless_dropped', entries: dropped })
      }
      // E o da entrada de outra conta que venceu o prazo (contagem, sem id nem dono).
      if (expiredOthers > 0) {
        track({ type: 'sync_group_rejected', ts: Math.floor(Date.now() / 1000), reason: 'other_owner_expired', entries: expiredOthers })
      }
      // E o da quarentena (contagem por motivo/status, sem id de perfil nem itemKey).
      for (const q of quarantined.values()) {
        track({
          type: 'sync_item_quarantined',
          ts: Math.floor(Date.now() / 1000),
          reason: q.reason,
          status: q.status,
          entries: q.entries,
        })
      }
      flushInflight = null
    }
  })()
  return flushInflight
}

/**
 * Liga os flushes oportunistas por evento (1x por sessão do app). Idempotente.
 * - visibilitychange→hidden: usuário saiu da aba/app em background.
 * - pagehide: navegador vai descarregar a página (último suspiro).
 */
export function bindFlushEvents(): void {
  if (listenersBound) return
  if (typeof document === 'undefined' || typeof window === 'undefined') return
  listenersBound = true

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flushNow()
  })
  // pagehide é mais confiável que beforeunload em mobile/TV.
  window.addEventListener('pagehide', () => {
    void flushNow()
  })
}
